import { responderMaqueta, usaMaqueta } from './maqueta';
/**
 * Cliente del portal.
 *
 * El navegador ya no conoce ninguna clave de la plataforma: manda una cookie
 * de sesión `httpOnly` que ni siquiera puede leer desde JavaScript. La clave
 * de consulta del tenant vive solo en el portal, que además decide qué rutas
 * y qué roles pueden alcanzar la API de datos.
 *
 * Todas las URLs son relativas a propósito: el portal sirve este dashboard, o
 * sea mismo origen, sin CORS y con la cookie marcada SameSite=Strict.
 */

export type Rol = 'admin' | 'analista' | 'lector';

export type EstadoFacturacion = 'al_dia' | 'gracia' | 'suspendido';

export interface FacturacionTenant {
  estado: EstadoFacturacion;
  /** Solo tiene sentido con `estado: 'gracia'`. */
  graciaHasta: string | null;
}

export interface TenantVisible {
  id: string;
  nombre: string;
  rol: Rol;
  /** Solo en el tenant activo: la lista para cambiar de cliente no la necesita. */
  facturacion?: FacturacionTenant;
}

export interface Sesion {
  usuario: {
    email: string;
    nombre: string;
    segundoFactor: boolean;
    codigosRespaldoRestantes: number;
  };
  tenant: TenantVisible;
  permisos: { puedeVerServicios: boolean; puedeAdministrar: boolean };
  tenants: TenantVisible[];
}

import type { Fuente } from './fuente';
import type { Veredicto as VeredictoRelease } from './releases';

export interface ProcesoResumen {
  process_name: string;
  ejecuciones: string;
  ok: string;
  con_error: string;
  abandonados: string;
  con_negocio?: string;
  p50_ms: number;
  p95_ms: number;
}

export interface PasoEmbudo {
  paso: string;
  indice: number;
  llegaron: number;
  errores: number;
  alcance_pct: number;
  caida_pct: number;
  p50_ms: number;
  p95_ms: number;
}

export interface Embudo {
  fuente: Fuente;
  proceso: string;
  ejecuciones: number;
  completados: number;
  con_error: number;
  abandonados: number;
  /** Terminaron porque el negocio dijo que no (`BUSINESS_ERROR`). Aparte de `con_error`. */
  con_negocio: number;
  pasos: PasoEmbudo[];
}

export interface CanalConocido {
  canal: string;
  /** Si tuvo trafico en la ventana consultada. Un canal en cero sigue estando. */
  conTrafico: boolean;
}

export interface CanalResumen {
  canal: string;
  ejecuciones: string;
  ok: string;
  con_error: string;
  abandonados: string;
  p50_ms: number;
  p95_ms: number;
}

export interface FilaSalud {
  canal: string;
  ejecuciones: number;
  tasa_error: number;
  /** Ausente si el servicio de consulta es anterior a `BUSINESS_ERROR`. */
  tasa_negocio?: number;
  tasa_abandono: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
}

/** Una llamada concreta a una ruta, para bajar del percentil al caso. */
export interface Llamada {
  trace_id: string;
  ts: string;
  duracion_ms: number;
  /** `Error` cuando falló: es el valor que ClickHouse guarda en `StatusCode`. */
  estado: string;
  canal: string;
}

export interface Hallazgo {
  id: string;
  titulo: string;
  severidad: 'alta' | 'media' | 'baja';
  /**
   * De dónde salió el hallazgo.
   *
   * `crash` faltaba acá y el servidor sí lo emite. En ejecución no rompía nada
   * —la comparación se hace igual en JavaScript— pero el compilador daba por
   * imposible la rama que muestra la traza del crash, que es la única forma de
   * ver un crash desde el informe.
   *
   * Este tipo es una copia parcial a mano del que declara el servidor en
   * `apps/query/src/analisis.ts`, y esto es lo que cuesta: agregar un valor
   * allá no obliga a nada acá. Se omite `anomalia`, que el servidor manda y el
   * panel no usa: la consume el servicio de alertas.
   */
  origen: 'anomalia' | 'cronico' | 'vital' | 'crash';
  sintoma: string;
  alcance: { clase: string; afectados: string[]; sanos: string[]; conclusion: string };
  causaRaiz: {
    clase: string;
    conclusion: string;
    atribucion: Array<{ servicio: string; operacion: string; propio_ms: number; pct: number }>;
    excepciones: Array<{ tipo: string; veces: number }>;
  } | null;
  acciones: Array<{ texto: string; porque: string }>;
  evidencia: { proceso: string; paso: string | null; ruta: string | null; trazas: string[] };
}

export interface Informe {
  generado: string;
  ventana: { desde: string; hasta: string };
  fuente: Fuente;
  salud: FilaSalud[];
  hallazgos: Hallazgo[];
  limitaciones: string[];
}

/**
 * La traza de un crash, ya resuelta por el servidor.
 *
 * Los campos varían según el tipo —un volcado nativo trae `faltantes`, una
 * excepción de la JVM trae `resueltos`— pero `texto` está siempre y es lo que
 * se muestra. Se tipa laxo a propósito: el panel no reconstruye el formato.
 */
export interface CrashResuelto {
  tipo: string;
  app_version: string;
  texto: string;
  aviso: string | null;
  senal?: string | null;
  emparejado?: 'map_id' | 'version' | null;
  map_id?: string | null;
  faltantes?: Array<{ nombre: string; buildId: string }>;
  resueltos?: number;
}

export interface EnlaceInforme {
  id: string;
  creadoPor: string;
  creado: string;
  expira: string;
  ventana: { desde: string; hasta: string };
  hallazgos: number;
  aperturas: number;
  ultimaApertura: string | null;
}

export interface EnlacePantalla {
  id: string;
  creadoPor: string;
  creado: string;
  expira: string;
}

export interface Release {
  app_version: string;
  ejecuciones: string;
  desde: string;
  hasta: string;
  canales: string[];
  plataformas: string[];
}

export interface Comparacion {
  a: number;
  b: number;
  delta: number;
  deltaRelativo: number | null;
  veredicto: VeredictoRelease;
  /** Falso en el p95: solo se le aplicaron pisos, no un contraste. */
  contrastada: boolean;
  motivo: string;
}

export interface FilaComparada {
  proceso: string;
  /** Vacío en la fila del proceso; con nombre en las de cada paso. */
  paso: string;
  indice: number;
  a: { ejecuciones: number; p50_ms: number; p95_ms: number };
  b: { ejecuciones: number; p50_ms: number; p95_ms: number };
  error: Comparacion;
  abandono: Comparacion | null;
  latencia: Comparacion;
  veredicto: VeredictoRelease;
}

export interface ComparacionReleases {
  a: string;
  b: string;
  filas: FilaComparada[];
  veredicto: VeredictoRelease;
  fuente: Fuente;
}

/** Un tramo de la actividad en el tiempo: los conteos llegan como texto. */
export interface PuntoSerie {
  t: string;
  ejecuciones: string;
  ok: string;
  /** Fallas, sin los rechazos de negocio. */
  con_error: string;
  con_negocio: string;
  abandonados: string;
  p95_ms: number;
}

export interface Serie {
  fuente: Fuente;
  paso_segundos: number;
  puntos: PuntoSerie[];
}

export interface ResumenProceso {
  proceso: string;
  ejecuciones: string;
  ok: string;
  con_error: string;
  con_negocio: string;
  abandonados: string;
  p95_ms: number;
}

export interface Ejemplo {
  trace_id: string;
  process_id: string;
  ts: string;
  result: string;
  duration_ms: number;
  platform: string;
  canal: string;
  app_version: string;
  /** El motivo del cierre; vacío si el proceso no lo declaró. */
  desenlace: string;
  /** Por qué terminó en error; vacío si no terminó en error. */
  causa: Causa | '';
  /** La llamada que mejor explica el cierre, si alguna falló. */
  llamada: string;
  /** Su código HTTP; 0 si no obtuvo respuesta. */
  codigo: number;
}

/**
 * Por qué terminó en error una ejecución: el negocio dijo que no, falló un
 * servicio, o ninguna llamada falló.
 */
export type Causa = 'servicio' | 'negocio' | 'otro';

export type Causas = Record<Causa, number>;

/**
 * Salud HTTP de una ruta, en tres grupos y no en dos.
 *
 * Juntar 4xx con 5xx dejaria la señal permanentemente en rojo por trafico
 * normal: en un portal de tarjetas un 409 es el servicio diciendo que el
 * cliente no califica. Cuales 4xx son normales lo declara cada cliente.
 */
export interface SaludHttp {
  servicio: string;
  ruta: string;
  metodo: string;
  llamadas: number;
  ok: number;
  /** 5xx: el servicio se rompio. */
  servidor: number;
  /** 4xx no declarados: 400, 404, 405, 415… casi siempre una integracion rota. */
  contrato: number;
  /** 4xx declarados como operacion normal. No es incidente. */
  negocio: number;
  tasa_servidor: number;
  tasa_contrato: number;
}

export interface Servicio {
  ruta: string;
  servicio: string;
  /** Presente solo cuando los percentiles vienen de histogramas. */
  canal?: string;
  llamadas: string;
  errores: string;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  promedio_ms?: number;
  /** Origenes de esta ruta: esquema, host y puerto. Puede ser mas de uno. */
  urls?: string[];
}

export interface Vital {
  vital: string;
  muestras: string;
  p50: number;
  p75: number;
  p95: number;
  buenos_pct: number;
  a_mejorar_pct: number;
  malos_pct: number;
}

export interface SpanTraza {
  Timestamp: string;
  SpanId: string;
  ParentSpanId: string;
  SpanName: string;
  SpanKind: string;
  ServiceName: string;
  Duration: string;
  StatusCode: string;
  StatusMessage: string;
  SpanAttributes: Record<string, string>;
}

export interface Asiento {
  ts: string;
  actor_id: string;
  actor_email: string;
  evento: string;
  resultado: string;
  recurso: string;
  ip: string;
  agente: string;
  detalle: string;
}

export interface ResumenActor {
  actor_email: string;
  eventos: string;
  ultimo: string;
}

export interface Miembro {
  id: string;
  email: string;
  nombre: string;
  rol: Rol;
  segundoFactor: boolean;
  esUnoMismo: boolean;
}

export interface Invitacion {
  id: string;
  email: string;
  rol: Rol;
  invitadaPor: string;
  creada: string;
  expira: string;
}

export interface DatosInvitacion {
  email: string;
  rol: string;
  invitadaPor: string;
  tenant: string;
  yaTieneCuenta: boolean;
  expira: string;
}

export interface Anomalia {
  tipo: 'linea_base' | 'version';
  metrica: string;
  ambito: string;
  canal: string;
  severidad: 'alta' | 'media' | 'baja';
  actual: number;
  esperado: number;
  cambioRelativo: number;
  muestras: number;
  explicacion: string;
  detalle: string;
}

export interface EstadoServicio {
  servicio: string;
  ok: boolean;
  commit: string | null;
  latenciaMs: number | null;
  detalle?: string;
}

export interface VersionReportada {
  canal: string;
  sdk: string;
  app: string;
  platform: string;
  spans: string;
  visto: string;
}

/** Un correo puede estar en más de una lista: no son un rango, son tres conjuntos independientes. */
export interface CorreosPorSeveridad {
  alta: string[];
  media: string[];
  baja: string[];
}

export interface ConfigAlertas {
  correosPorSeveridad: CorreosPorSeveridad;
  webhook: string;
  tieneSecreto: boolean;
  actualizado: string | null;
  /** Solo viene en la respuesta que lo genera: después queda cifrado. */
  secreto?: string;
}

export class ErrorApi extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/** Un 401 significa que la sesión venció: hay que volver a la pantalla de acceso. */
export const esSesionVencida = (err: unknown): boolean => err instanceof ErrorApi && err.status === 401;

async function pedir<T>(
  ruta: string,
  opciones: { metodo?: string; cuerpo?: unknown; params?: Record<string, string> } = {}
): Promise<T> {
  const url = new URL(ruta, window.location.origin);
  for (const [k, v] of Object.entries(opciones.params ?? {})) url.searchParams.set(k, v);

  // Datos de muestra, solo en desarrollo y con `?maqueta`: ver `maqueta.ts`.
  if (import.meta.env.DEV && usaMaqueta()) {
    const respuesta = responderMaqueta(url);
    if (respuesta !== undefined) return respuesta as T;
  }

  const respuesta = await fetch(url, {
    method: opciones.metodo ?? 'GET',
    // Sin esto la cookie de sesión no viaja.
    credentials: 'same-origin',
    ...(opciones.cuerpo !== undefined
      ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(opciones.cuerpo) }
      : {}),
  });

  if (!respuesta.ok) {
    const cuerpo = (await respuesta.json().catch(() => ({}))) as { error?: string };
    throw new ErrorApi(cuerpo.error ?? `error ${respuesta.status}`, respuesta.status);
  }
  return (await respuesta.json()) as T;
}

function rango(desdeISO: string, hastaISO?: string): Record<string, string> {
  return { from: desdeISO, to: hastaISO ?? new Date().toISOString() };
}

/** Debe coincidir con LARGO_MINIMO_CONTRASENA del portal. */
export const LARGO_MINIMO_CONTRASENA = 12;

export const api = {
  /** Devuelve la sesión, o `{ requiere2fa: true }` si falta el segundo factor. */
  entrar: (email: string, contrasena: string) =>
    pedir<Sesion | { requiere2fa: true }>('/auth/login', {
      metodo: 'POST',
      cuerpo: { email, contrasena },
    }),

  verificarSegundoFactor: (codigo: string) =>
    pedir<Sesion>('/auth/2fa/verificar', { metodo: 'POST', cuerpo: { codigo } }),

  iniciarSegundoFactor: () =>
    pedir<{ secreto: string; uri: string; qr: string }>('/auth/2fa/iniciar', { metodo: 'POST' }),

  activarSegundoFactor: (codigo: string) =>
    pedir<{ ok: boolean; codigosRespaldo: string[] }>('/auth/2fa/activar', {
      metodo: 'POST',
      cuerpo: { codigo },
    }),

  desactivarSegundoFactor: (contrasena: string, codigo: string) =>
    pedir<{ ok: boolean }>('/auth/2fa/desactivar', {
      metodo: 'POST',
      cuerpo: { contrasena, codigo },
    }),

  salir: () => pedir<{ ok: boolean }>('/auth/logout', { metodo: 'POST' }),

  recuperar: (email: string) =>
    pedir<{ ok: boolean; mensaje: string }>('/auth/recuperar', { metodo: 'POST', cuerpo: { email } }),

  restablecer: (token: string, contrasena: string) =>
    pedir<{ ok: boolean; mensaje: string }>('/auth/restablecer', {
      metodo: 'POST',
      cuerpo: { token, contrasena },
    }),

  cambiarContrasena: (actual: string, nueva: string) =>
    pedir<{ ok: boolean; sesionesCerradas: number }>('/auth/contrasena', {
      metodo: 'POST',
      cuerpo: { actual, nueva },
    }),

  sesion: () => pedir<Sesion>('/auth/yo'),

  equipo: () => pedir<{ miembros: Miembro[]; invitaciones: Invitacion[] }>('/auth/equipo'),

  /** Qué commit corre en cada servicio, preguntado en vivo por el portal. */
  despliegue: () => pedir<{ servicios: EstadoServicio[] }>('/auth/despliegue'),

  /** Qué versión del SDK y de la app reporta cada canal, según los datos. */
  versionesReportadas: () => pedir<{ versiones: VersionReportada[] }>('/api/versiones'),

  configAlertas: () => pedir<ConfigAlertas>('/auth/alertas'),

  guardarConfigAlertas: (datos: {
    correosAlta: string[];
    correosMedia: string[];
    correosBaja: string[];
    webhook: string;
    regenerarSecreto?: boolean;
  }) => pedir<ConfigAlertas>('/auth/alertas', { metodo: 'PUT', cuerpo: datos }),

  /** Manda una alerta de muestra por las vías ya guardadas: correo y webhook, cada una por su lado. */
  probarAlertas: () =>
    pedir<{
      correo: null | { enviados: string[]; via: string; llego: boolean; error?: string };
      webhook: null | { url: string; ok: boolean; estado?: number; motivo?: string };
    }>('/auth/alertas/prueba', { metodo: 'POST' }),

  invitar: (email: string, rol: string) =>
    pedir<{ invitacion: Invitacion; enlace: string; correoEnviado: boolean }>('/auth/invitaciones', {
      metodo: 'POST',
      cuerpo: { email, rol },
    }),

  revocarInvitacion: (id: string) =>
    pedir<{ ok: boolean }>(`/auth/invitaciones/${encodeURIComponent(id)}`, { metodo: 'DELETE' }),

  quitarMiembro: (usuarioId: string) =>
    pedir<{ ok: boolean; sesionesCerradas: number }>(
      `/auth/miembros/${encodeURIComponent(usuarioId)}`,
      { metodo: 'DELETE' }
    ),

  cambiarRolMiembro: (usuarioId: string, rol: string) =>
    pedir<{ ok: boolean; rol: string; sesionesCerradas: number }>(
      `/auth/miembros/${encodeURIComponent(usuarioId)}`,
      { metodo: 'PATCH', cuerpo: { rol } }
    ),

  verInvitacion: (token: string) =>
    pedir<DatosInvitacion>(`/auth/invitacion/ver/${encodeURIComponent(token)}`),

  aceptarInvitacion: (token: string, nombre?: string, contrasena?: string) =>
    pedir<{ ok: boolean; cuentaNueva: boolean; requiere2fa?: boolean }>('/auth/invitacion/aceptar', {
      metodo: 'POST',
      cuerpo: { token, ...(nombre ? { nombre } : {}), ...(contrasena ? { contrasena } : {}) },
    }),

  cambiarTenant: (tenantId: string) =>
    pedir<Sesion>('/auth/tenant', { metodo: 'POST', cuerpo: { tenantId } }),

  procesos: (desde: string, canal?: string) =>
    pedir<{ procesos: ProcesoResumen[]; fuente: Fuente }>('/api/procesos', {
      params: { ...rango(desde), ...(canal ? { canal } : {}) },
    }),

  canales: (desde: string, proceso?: string) =>
    pedir<{ disponibles: CanalConocido[]; comparativa: CanalResumen[]; fuente: Fuente }>(
      '/api/canales',
      { params: { ...rango(desde), ...(proceso ? { proceso } : {}) } }
    ),

  analisis: (desde: string) => pedir<Informe>('/api/analisis', { params: rango(desde) }),

  /** Bajo demanda: resolver un volcado cuesta, y casi nunca se miran todos. */
  crash: (traceId: string) => pedir<CrashResuelto>(`/api/crash/${encodeURIComponent(traceId)}`),

  enlacesInforme: () => pedir<{ enlaces: EnlaceInforme[] }>('/informe/enlaces'),

  /** Devuelve la URL una única vez: después solo se puede revocar. */
  crearEnlaceInforme: (desde: string) =>
    pedir<EnlaceInforme & { url: string }>('/informe/enlaces', {
      metodo: 'POST',
      cuerpo: {},
      params: rango(desde),
    }),

  revocarEnlaceInforme: (id: string) =>
    pedir<{ revocado: boolean }>(`/informe/enlaces/${encodeURIComponent(id)}`, {
      metodo: 'DELETE',
    }),

  enlacesPantalla: () => pedir<{ enlaces: EnlacePantalla[] }>('/pantalla/enlaces'),

  /** Devuelve la URL una única vez: después solo se puede revocar. */
  crearEnlacePantalla: () =>
    pedir<EnlacePantalla & { url: string }>('/pantalla/enlaces', { metodo: 'POST', cuerpo: {} }),

  revocarEnlacePantalla: (id: string) =>
    pedir<{ revocado: boolean }>(`/pantalla/enlaces/${encodeURIComponent(id)}`, {
      metodo: 'DELETE',
    }),

  releases: (desde: string, canal?: string) =>
    pedir<{ releases: Release[]; fuente: Fuente }>('/api/releases', {
      params: { ...rango(desde), ...(canal ? { canal } : {}) },
    }),

  compararReleases: (a: string, b: string, desde: string, canal?: string) =>
    pedir<ComparacionReleases>('/api/comparar', {
      params: { ...rango(desde), a, b, ...(canal ? { canal } : {}) },
    }),

  /** Marca del último dato del tenant, sin filtro de tiempo. Explica los vacíos. */
  ultimo: () => pedir<{ ultimo: string | null }>('/api/ultimo'),

  /** Sin ventana: la detección usa su propio horizonte. */
  anomalias: () => pedir<{ anomalias: Anomalia[] }>('/api/anomalias'),

  embudo: (proceso: string, desde: string, canal?: string) =>
    pedir<Embudo>('/api/embudo', { params: { ...rango(desde), proceso, ...(canal ? { canal } : {}) } }),

  /** Llamadas concretas a una ruta, de la más lenta a la menos. */
  llamadas: (servicio: string, ruta: string, desde: string, canal?: string) =>
    pedir<{ llamadas: Llamada[]; fuera_de_retencion: boolean }>('/api/llamadas', {
      params: { ...rango(desde), servicio, ruta, ...(canal ? { canal } : {}) },
    }),

  ejemplos: (
    proceso: string,
    desde: string,
    resultado?: string,
    canal?: string
  ) =>
    pedir<{ ejemplos: Ejemplo[]; sin_ejemplos: string | null }>('/api/ejemplos', {
      params: {
        ...rango(desde),
        proceso,
        ...(resultado ? { resultado } : {}),
        ...(canal ? { canal } : {}),
      },
    }),

  /** La actividad en el tiempo, de todos los procesos o de uno. */
  serie: (desde: string, hasta?: string, canal?: string, proceso?: string) =>
    pedir<Serie>('/api/serie', {
      params: { ...rango(desde, hasta), ...(canal ? { canal } : {}), ...(proceso ? { proceso } : {}) },
    }),

  /** Cada proceso con sus fallas y sus rechazos de negocio por separado. */
  resumen: (desde: string, hasta?: string, canal?: string) =>
    pedir<{ fuente: Fuente; procesos: ResumenProceso[] }>('/api/resumen', {
      params: { ...rango(desde, hasta), ...(canal ? { canal } : {}) },
    }),

  /** Las últimas ejecuciones que no terminaron bien, de todos los procesos. */
  recientes: (desde: string, canal?: string) =>
    pedir<{ ejecuciones: Array<Ejemplo & { process_name: string }>; sin_ejemplos: string | null }>(
      '/api/recientes',
      { params: { ...rango(desde), ...(canal ? { canal } : {}) } }
    ),

  /** De qué lado fueron los errores de un proceso: negocio, servicio u otro. */
  causas: (proceso: string, desde: string, canal?: string) =>
    pedir<{ causas: Causas | null }>('/api/desenlaces', {
      params: { ...rango(desde), proceso, ...(canal ? { canal } : {}) },
    }),

  servicios: (desde: string, canal?: string) =>
    pedir<{
      servicios: Servicio[];
      http?: SaludHttp[];
      advertencia?: string;
      fuente?: string;
    }>('/api/servicios', {
      params: { ...rango(desde), ...(canal ? { canal } : {}) },
    }),

  vitals: (desde: string, canal?: string) =>
    pedir<{ vitals: Vital[] }>('/api/vitals', {
      params: { ...rango(desde), ...(canal ? { canal } : {}) },
    }),

  traza: (traceId: string) => pedir<{ spans: SpanTraza[] }>(`/api/trazas/${traceId}`),

  auditoria: (desde: string, evento?: string, actor?: string) =>
    pedir<{ asientos: Asiento[] }>('/api/auditoria', {
      params: { ...rango(desde), limite: '300', ...(evento ? { evento } : {}), ...(actor ? { actor } : {}) },
    }),

  auditoriaActores: (desde: string) =>
    pedir<{ actores: ResumenActor[] }>('/api/auditoria-actores', { params: rango(desde) }),
};

// Viven en ventanas.ts, que no importa nada y por eso se puede probar con el
// ejecutor de tests de Node. Se reexportan para no cambiar quien ya las usa.
export { desdeISO, VENTANAS } from './ventanas';
